import { useUserImage } from '@/_shared/context/UserImageContext';
import { IdentityService } from '@/_shared/services';
import container from '@/_shared/services/service-container';
import { imageAntiCaching } from '@/_shared/utils/image.util';
import { LogOut, User } from 'lucide-react';
import { useSession } from 'next-auth/react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';

export const ProfileMenu: React.FC = () => {
  const identityService = container.get<IdentityService>('IdentityService');
  const { data: session } = useSession();
  const { userImage, setUserImage } = useUserImage();
  const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const profileIconRef = useRef<HTMLImageElement | null>(null);
  const router = useRouter();

  const { id, firstName, lastName } = { ...session?.user };

  const getDetailsCallBack = useCallback(() => {
    async function getUserDetails() {
      if (id != null) {
        const response = await identityService.getUserDetails(id);
        return response;
      }
    }
    getUserDetails().then((data) => {
      if (data?.data) {
        setUserImage(data!.data.image);
      }
    });
  }, [identityService, id, setUserImage]);

  useEffect(() => {
    if (router.isReady) {
      getDetailsCallBack();
    }
  }, [getDetailsCallBack, router.isReady]);

  const toggleDropdown = (): void => {
    if (dropdownVisible) {
      document.removeEventListener('mousedown', handleClickOutside);
    } else {
      document.addEventListener('mousedown', handleClickOutside);
    }
    setDropdownVisible(!dropdownVisible);
  };

  const handleSignOut = async (): Promise<void> => {
    await identityService.logout();
  };

  const handleClickOutside = (event: MouseEvent): void => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      profileIconRef.current &&
      !profileIconRef.current.contains(event.target as Node)
    ) {
      setDropdownVisible(false);
      document.removeEventListener('mousedown', handleClickOutside);
    }
  };

  return (
    <div className="relative">
      {session && session.user && (
        <div>
          <div className="group rounded-full w-6 h-6 overflow-hidden">
            <Image
              loader={() =>
                userImage
                  ? `${userImage}?timestamp=${imageAntiCaching()}`
                  : `https://ui-avatars.com/api/?rounded=true&name=${
                      firstName + ' ' + lastName
                    }&background=1D1752&color=fff`
              }
              src={`https://ui-avatars.com/api/?rounded=true&name=${
                firstName + ' ' + lastName
              }&background=1D1752&color=fff`}
              alt={'Account picture'}
              width={100}
              height={100}
              className="rounded-full w-6 h-6 cursor-pointer object-cover group-hover:scale-105 transition-all"
              onClick={toggleDropdown}
              ref={profileIconRef}
            />
          </div>
          {dropdownVisible && (
            <div
              ref={dropdownRef}
              className="absolute right-0 mt-2 w-48 bg-white border border-gray-border rounded-xl shadow-lg z-50 text-sm font-medium transition-transform transform origin-top-right opacity-0 animate-dropdown"
            >
              <ul>
                <li>
                  <Link
                    className="flex items-center gap-2 w-full text-left px-4 py-2 rounded-t-lg text-gray-800 hover:bg-indigo-100"
                    href="/my-account"
                    onClick={toggleDropdown}
                  >
                    <User className="w-4" />
                    <span>My Account</span>
                  </Link>
                </li>
                <hr className="border-gray-border" />
                <li>
                  <button
                    className="flex items-center gap-2 text-red-600 px-4 py-2 rounded-b-lg hover:bg-red-100 w-full text-left"
                    onClick={handleSignOut}
                  >
                    <LogOut className="w-4" />
                    <span>Logout</span>
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileMenu;
